import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ColdStartGate
 *
 * Prevents the brief flash of the public/landing/auth screen on cold app
 * launch while Supabase is still restoring a stored session.
 *
 * Behavior:
 *  - On the very first auth resolution of this JS session, renders nothing
 *    while `auth.loading === true`. As soon as `loading` flips to false (or
 *    a 3s safety timeout fires) the gate releases permanently and never
 *    blanks the app again, even on token refreshes or sign-out.
 *  - Routes that must execute their handler regardless of auth state
 *    (OAuth callbacks, native bridges, etc.) are exempt.
 *
 * This is a pure render gate: it does not touch auth, routing, or data.
 */

// Routes that must always render, even before the first auth tick resolves,
// because they perform the auth handshake themselves.
const EXEMPT_PREFIXES = [
  "/oauth/",
  "/auth-bridge",
  "/auth/apple/callback",
  "/auth/error",
  "/native-callback",
  "/app-auth", // includes free-signup and complete-registration
  "/complete-registration",
  "/payment-success",
  "/auth-loading",
  "/shop/supplier-update",
];

// Hard safety net: never blank the app for more than this, no matter what.
const MAX_BLANK_MS = 3000;

// Module-level so the gate fires at most once per JS session.
let coldStartResolved = false;

function isExempt(pathname: string): boolean {
  return EXEMPT_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export function ColdStartGate({ children }: { children: ReactNode }) {
  const { loading } = useAuth();
  const location = useLocation();
  const [released, setReleased] = useState(coldStartResolved);

  // Release as soon as the first auth resolution lands.
  useEffect(() => {
    if (released) return;
    if (!loading) {
      coldStartResolved = true;
      setReleased(true);
    }
  }, [loading, released]);

  /* Y con la app ya en pie, fuera la pantalla de relevo del `index.html`. Se
     quita AQUÍ y no al montar React porque este es el momento en que hay algo
     que enseñar: quitarla antes solo cambiaría el logo por un blanco liso, que
     es justo el hueco que viene a tapar. Ver el comentario del `<body>`. */
  useEffect(() => {
    if (!released) return;
    (window as unknown as { __ultureQuitarSplash?: () => void }).__ultureQuitarSplash?.();
  }, [released]);

  // Safety timeout — if auth never resolves (network down, etc.) we still
  // show the app rather than blanking forever.
  useEffect(() => {
    if (released) return;
    const t = setTimeout(() => {
      coldStartResolved = true;
      setReleased(true);
    }, MAX_BLANK_MS);
    return () => clearTimeout(t);
  }, [released]);

  if (!released && !isExempt(location.pathname)) {
    // Intentionally blank — matches the app background so the cold-start
    // flicker of the logged-out screen disappears.
    return <div className="min-h-screen bg-background" aria-hidden="true" />;
  }

  return <>{children}</>;
}

export default ColdStartGate;
